import {
	IonButton,
	IonButtons,
	IonCheckbox,
	IonContent,
	IonFooter,
	IonHeader,
	IonIcon,
	IonLabel,
	IonModal,
	IonPage,
	IonToolbar,
	IonSearchbar,
	isPlatform,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import {
	arrowBackOutline,
	cameraOutline,
	refreshOutline,
} from "ionicons/icons";
import usePartSearch from "../../../context/search.context";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { CustomCamera } from '../../../components/custom-camera';
import { isEmpty, isNil } from 'lodash';
import "./SearchPage.css";
import { useSettingsContext } from '../../../context';
import { CAMERA_OVERLAY_STYLE } from '../../../constants';

const HEIGHT_RATIO = "100%";

export function SearchPage() {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [saveX, setSaveX] = useState<number>();
	const [saveY, setSaveY] = useState<number>();
	const [coords, setCoords] = useState<number[][]>([]);
	const [isTextSearch, setIsTextSearch] = useState<boolean>(false);
	const [textSearch, setTextSearch] = useState<string>("");
	const [isOpen, setIsOpen] = useState<boolean>(true);

	const { currentImage, searchId, startSearch, startTextSearch, setCurrentImage } = usePartSearch();
	const history = useHistory();
	const { theme } = useSettingsContext();
	const { t } = useTranslation();

	useEffect(() => {
		if (!currentImage) {
			cleanCanvas();
			setIsOpen(true);
		} else if (currentImage.blobPath!) {
			drawImage(currentImage.blobPath!);
		}
	}, [searchId]);

	function onSearch() {
		if (currentImage) {
			startSearch(coords, isTextSearch);
			history.push("/search-results");
		}
		if (textSearch) {
			startTextSearch(textSearch);
			history.push("/search-results");
		}
	}

	function onTextSearch() {
		setIsTextSearch(!isTextSearch);
	}

	function cleanCanvas() {
		const canvas = canvasRef.current;
		if (canvas) {
			const context = canvas.getContext("2d");
			if (context) {
				context.clearRect(0, 0, canvas.width, canvas.height);
			}
		}
	}

	function drawImage(imageWebPath: string) {
		const canvas = canvasRef.current;
		if (canvas) {
			const context = canvas.getContext("2d");
			if (context) {
				const baseImage = new Image();
				baseImage.src = imageWebPath;

				baseImage.onload = function () {
					canvas.width = baseImage.width;
					canvas.height = baseImage.height;
					context.drawImage(baseImage, 0, 0, baseImage.width, baseImage.height);

					const fileName = `search-photo-${new Date().getTime()}.jpeg`;
					canvas.toBlob((blob) => {
						if (!blob) return;

						const image = {
							blobPath: window.URL.createObjectURL(blob),
							filepath: fileName,
						}
						setCurrentImage(image);
					});
				};
			}
		}
	}

	function startDrawing(ev: any) {
		const canvas = canvasRef.current;
		if (canvas) {
			const rect = canvas.getBoundingClientRect();
			if (isNil(ev.touches) || isNil(ev.touches[0])) return;
			const touch = ev.touches[0];
			const x_rel = touch.clientX - rect.left;
			const y_rel = touch.clientY - rect.top;

			const newX = Math.round((x_rel * ev.target.width) / rect.width);
			const newY = Math.round((y_rel * ev.target.height) / rect.height);

			setSaveX(newX);
			setSaveY(newY);

			const oldX = touch.pageX - rect.left;
			const oldY = touch.pageY - rect.top;

			const newCoords = [...coords];
			newCoords.push([oldX / rect.width, oldY / rect.height]);
			setCoords(newCoords);
		}
	}

	function updateLine(ev: any) {
		const canvas = canvasRef.current;

		if (canvas) {
			const context = canvas.getContext("2d");

			if (context && saveX && saveY) {
				const rect = canvas.getBoundingClientRect();

				if (isNil(ev.touches) || isNil(ev.touches[0])) return;

				const touch = ev.touches[0];
				const x_rel = touch.clientX - rect.left;
				const y_rel = touch.clientY - rect.top;

				const currentX = Math.round((x_rel * ev.target.width) / rect.width);
				const currentY = Math.round((y_rel * ev.target.height) / rect.height);

				context.lineJoin = "round";
				context.lineWidth = 5;

				context.beginPath();
				context.moveTo(saveX, saveY);
				context.lineTo(currentX, currentY);
				context.closePath();

				context.stroke();

				setSaveX(currentX);
				setSaveY(currentY);

				const oldX = touch.pageX - rect.left;
				const oldY = touch.pageY - rect.top;

				const newCoords = [...coords];
				newCoords.push([oldX / rect.width, oldY / rect.height]);
				setCoords(newCoords);
			}
		}
	}

	function onBack() {
		history.push("/landing");
	}

	function resetDrawing() {
		if (!isNil(currentImage) && !isNil(currentImage.blobPath)) {
			drawImage(currentImage.blobPath);
		}
		setCoords([]);
	}

	function onDismissCamera(photo: string) {
		if (isNil(photo) || isEmpty(photo)) {
			console.log("Camera view cancelled");
			setIsOpen(false);
			return;
		}
		drawImage(photo);

		setIsOpen(false);
	}

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						<IonButton onClick={onBack} size="large">
							<IonIcon icon={arrowBackOutline} slot="icon-only" />
						</IonButton>
					</IonButtons>
					<IonSearchbar
						placeholder="Text search..."
						onIonChange={(e) => setTextSearch(e.detail.value!)}
						value={textSearch}
					/>
				</IonToolbar>

			</IonHeader>
			<IonContent
				fullscreen
				scrollY={false}
			>
				<IonModal
					isOpen={isOpen}
					onDidDismiss={() => setIsOpen(false)}
					style={CAMERA_OVERLAY_STYLE}
				>
					<CustomCamera
						onDismiss={onDismissCamera}
					/>
				</IonModal>
				<canvas
					onMouseDown={startDrawing}
					onMouseMove={updateLine}
					onTouchMove={updateLine}
					onTouchStart={startDrawing}
					ref={canvasRef}
					style={{
						display: "block",
						marginLeft: "auto",
						marginRight: "auto",
						paddingLeft: 0,
						paddingRight: 0,
						height: HEIGHT_RATIO,
						// width: `calc(${HEIGHT_RATIO} * ${ratio})`,
					}}
				/>
			</IonContent>
			<IonFooter>
				<div className='checkbox-container'>
					<IonLabel color={theme === "dark" ? "white" : "dark"}>
						{t("searchPage.textSearchCheckbox")}
					</IonLabel>
					<IonCheckbox
						className='checkbox-item'
						checked={isTextSearch}
						onIonChange={onTextSearch}
						slot="end"
					/>
				</div>
				<IonButton
					disabled={coords.length === 0}
					color="primary"
					expand="block"
					onClick={resetDrawing}
					size="small"
				>
					<IonIcon icon={refreshOutline} slot="end" />
					{t("searchPage.resetDrawing")}
				</IonButton>
				<IonButton
					color="primary"
					expand="block"
					onClick={() => setIsOpen(true)}
					size="small"
				>
					<IonIcon
						icon={cameraOutline}
						slot="end"
					/>
					{t("searchPage.retakePhoto")}
				</IonButton>
				<IonButton
					color="primary"
					disabled={textSearch === "" && isNil(currentImage)}
					expand="block"
					onClick={onSearch}
					size="small"
				>
					{t("searchPage.search")}
				</IonButton>
			</IonFooter>
		</IonPage>
	);
}
